import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import axios from 'src/axios';
import {
  APICONSTANTS,
  HPO_CONSTANTS,
  PEDIGREE
} from 'src/components/Constants';
import { getHPOSearchURL } from 'src/util/util';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import TextInput from 'src/components/UI/input/TextInput';
import { GENDER, PREGNANCY_OUTCOME } from '../../pedigree/config';
import { addChildrenToRecords } from '../../pedigree/edgeIntegrity';
import {
  relationship,
  getRelationshipForNewPerson
} from '../../pedigree/utils';
import {
  deriveConception,
  conceptionToAxes,
  conceptionValidationError,
  donorIdentityValidationError,
  donorSexWarning
} from '../../pedigree/pregnancyConception';
import { deriveObstetrics } from '../../pedigree/pregnancyObstetrics';
import { normalizeFetuses } from '../../pedigree/pregnancyNormalize';
import { donorRoleGender } from '../../pedigree/deriveReproNodes';
import {
  newbornInheritance,
  newbornDefaultGender,
  matchesMemberRef,
  stillbirthPersonFields,
  withStillbirthNote
} from '../../pedigree/liveBirthInheritance';
import {
  buildFetusDiagnosis,
  buildFetusPhenotype,
  isPhenotypeTermId,
  isGeneTermId
} from '../../pedigree/fetusDiagnoses';
import { PRENATAL_NOTE_TYPES } from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/prenatalScope';
import { twinFieldsFor, linkTwinMembers } from '../../pedigree/twinLinkage';
import { createPerson } from './AddRelativeMenu';

// A new, empty fetus. Sex defaults to NOT known (Bennett draws an unknown-sex pregnancy as a
// diamond) and the outcome to ongoing, matching how a clinician adds one mid-pregnancy.
const blankFetus = () => ({
  name: '',
  outcome: PREGNANCY_OUTCOME.ongoing,
  fetalSexKnown: false,
  gender: GENDER.female,
  sexDeterminationMethod: '',
  karyotype: '',
  pedigreeSymbolText: '',
  // null, not '' — the date field treats an empty string as an invalid value and wipes itself
  dob: null,
  dateAborted: null,
  hideInPedigree: false,
  comment: ''
});

// A stored date (Date | ISO string) → a Date, which is what the app's date input takes. `null` for
// empty: the picker treats '' as an invalid value and wipes the field.
const toDateValue = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// A derived EDD comes back as a Date (or the placeholder string when it could not be computed).
// Show a real date in the local form; pass a placeholder straight through.
const fmtDerivedDate = (v) =>
  v instanceof Date ? v.toLocaleDateString('en-GB') : v;

// The app's shared date field. `TextInput type="date"` delegates to `UI/input/DateInput`, an MUI X
// DatePicker whose contract is a DATE in and a DATE straight to onChange — NOT a change event.
// Handling it as an event (`e.target.value`) is what broke QA: picking a date did nothing, and
// typing cleared the day/month the moment the year was reached, because every keystroke wrote
// undefined back into the controlled value.
const DateField = ({ label, value, onChange, disableFuture }) => (
  <TextInput
    type="date"
    label={label}
    variant="outlined"
    size="small"
    fullWidth
    value={value ?? null}
    onChange={(date) => onChange(date ?? null)}
    disableFuture={disableFuture}
  />
);

// One read-only derived value, with its explanatory placeholder when it cannot be computed.
const DerivedRow = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 1,
      px: 1,
      py: 0.25
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value || '—'}
    </Typography>
  </Box>
);

// Shared reproductive-editor pieces (Track B) used by BOTH the pedigree context menu
// (ReproductiveMenu) and the carrier's "Reproductive" sidebar accordion
// (ReproductiveSection): the clinician pregnancy form, a delete confirm, and the CRUD hook
// that writes the Pregnancy record (via the carrier-agnostic /proband/pregnancy endpoints)
// and patches the carrier's member.pregnancies in state so the derivation re-renders.

export const OUTCOME_LABELS = [
  [PREGNANCY_OUTCOME.ongoing, 'Ongoing'],
  [PREGNANCY_OUTCOME.sab, 'Miscarriage (SAB)'],
  [PREGNANCY_OUTCOME.ectopic, 'Ectopic (ECT)'],
  [PREGNANCY_OUTCOME.top, 'Termination (TOP)'],
  [PREGNANCY_OUTCOME.stillbirth, 'Stillbirth (SB)'],
  [PREGNANCY_OUTCOME.liveBirth, 'Live birth']
];

// The carrier's in-graph spouses as { ref, label } for the "Conceived with a partner" picker.
// ref is the spouse's stable id (_id, else pid) — what otherParentMemberId stores and
// deriveReproNodes resolves via findByMemberId.
export const spouseOptionsFor = (carrier, memberRecords) =>
  (carrier?.spouseIds || [])
    .map((pid) => (memberRecords || []).find((m) => m.pid === pid))
    .filter(Boolean)
    .map((s) => ({
      ref: s._id || s.pid,
      // The pid alongside the ref, because the two are not interchangeable: `ref`
      // is what otherParentMemberId stores (_id once saved), while the Add
      // Relative panel needs a PID to pass as coParentPid — addChildrenToRecords
      // matches it against `spouseIds`, which holds pids. Additive; existing
      // consumers read ref/label/gender and are unaffected.
      pid: s.pid,
      label:
        [s.firstName, s.lastName].filter(Boolean).join(' ') ||
        `Person ${s.pid}`,
      gender: s.gender // sex assigned at birth — drives the sperm-source rule
    }));

// WHICH FETUS OUTCOMES CROSS THE FETUS→PERSON BOUNDARY.
//
// Live birth always did. TGCLOUD-1616 added stillbirth on Kathryn's ruling: "stillbirth is a full
// outcome and should still become a person, with a line through as deceased."
//
// Deliberately NOT the loss outcomes (miscarriage, ectopic, termination). Those are Bennett loss
// symbols rather than individuals, and Kathryn confirmed the triangle behaviour is already correct.
export const CONVERTIBLE_OUTCOMES = [
  PREGNANCY_OUTCOME.liveBirth,
  PREGNANCY_OUTCOME.stillbirth
];

// Can this fetus still become a person? Convertible outcome, and not already converted.
export const bornEligible = (f) =>
  !!f && CONVERTIBLE_OUTCOMES.includes(f.outcome) && !f.bornMemberId;

// The indexes the form offers a conversion for.
export const convertibleFetusIndexes = (fetuses) =>
  (Array.isArray(fetuses) ? fetuses : [])
    .map((f, i) => (bornEligible(f) ? i : -1))
    .filter((i) => i !== -1);

// Does the pregnancy form show its persistent "not on the pedigree yet" callout?
//
// TGCLOUD-1629 asked for an EXISTING stillbirth to be offered conversion when its pregnancy is next
// opened. Exported so that is provable rather than assumed: because normalizeFetuses lifts a legacy
// record's own outcome onto a synthesised singleton fetus, a stillbirth recorded before 1616 — with
// no `fetuses` array at all — already satisfies bornEligible and already gets the offer. No separate
// prompt was needed; what was missing was a test saying so.
//
// `record.bornMemberId` is the LEGACY pregnancy-level link: it only vetoes a single-fetus record,
// because on a multiple it mirrors the first fetus and must not block converting the second.
export const canOfferConversion = ({ fetuses, record, hasHandler }) =>
  !!hasHandler &&
  convertibleFetusIndexes(fetuses).length > 0 &&
  ((Array.isArray(fetuses) ? fetuses.length : 0) > 1 ||
    !(record && record.bornMemberId));

// The persistent callout's wording, for fetuses that are convertible but not yet converted. It has
// to cope with a MIXED pair since TGCLOUD-1616 — a live-birth + stillbirth twin pregnancy is
// exactly the case Kathryn demonstrated — so it names the outcome only where they all agree.
// Exported for its own test: the mixed wording is easy to get wrong and impossible to see in QA
// without building that specific pregnancy.
export const calloutText = (eligibleIndexes, fetuses) => {
  const outcomes = new Set(
    eligibleIndexes.map((i) => fetuses[i]?.outcome).filter(Boolean)
  );
  const allStillbirth =
    outcomes.size === 1 && outcomes.has(PREGNANCY_OUTCOME.stillbirth);
  const allLiveBirth =
    outcomes.size === 1 && outcomes.has(PREGNANCY_OUTCOME.liveBirth);
  if (eligibleIndexes.length === 1) {
    if (allStillbirth) {
      return 'Recorded as a stillbirth — the child is not on the pedigree yet.';
    }
    return 'Recorded as a live birth — the child is not on the pedigree yet.';
  }
  const noun = allStillbirth
    ? 'stillbirths'
    : allLiveBirth
    ? 'live births'
    : 'delivered'; // mixed — do not assert one outcome for both
  return `Recorded as ${noun} — ${eligibleIndexes.length} children are not on the pedigree yet.`;
};

// Asked when a saved record is a live birth whose child is not on the pedigree yet.
//
// TGCLOUD-1617 item 9 — Kathryn: "when we click save, we get 'this pregnancy is recorded, do we want
// to add the person', and then it's just kind of a yes or no, rather than clicking son and daughter at
// this point." So this is now a plain confirm: the sex comes from the FETUS, which item 8 made a
// first-class field on the form.
//
// This supersedes the earlier design where the prompt itself asked for the sex, on the reasoning that a
// prenatal sex call can be wrong and the sex at birth is the observation that counts. That reasoning
// still holds — the override just moved to the fetus field, where it is editable and persisted, rather
// than living in a one-shot prompt. Nothing is lost, and the sex is no longer recorded in two places.
//
// 'Not now' leaves the record flagged as a live birth with no linked person, addable later via the
// persistent callout in the form.
//
// TGCLOUD-1616 — the same prompt now also covers a STILLBIRTH conversion, which needs different
// words (the person is added deceased, not "the child added to the pedigree") and Kathryn's
// non-blocking warnings: "user should be prompted to indicate gender and DOB (still allow to
// progress if not provided but should at least get warning message)". So a missing sex or date is
// SAID, clearly, and the confirm button stays enabled — a stillbirth is often recorded with
// neither, and refusing the conversion would make those families unrecordable.
function LiveBirthConfirm({
  onConfirm,
  onClose,
  busy,
  gender,
  isStillbirth = false,
  dob = null
}) {
  const known = gender === GENDER.male || gender === GENDER.female;
  const missing = [
    !known ? 'sex' : null,
    isStillbirth && !dob ? 'date' : null
  ].filter(Boolean);
  return (
    <CustomDialog
      open={true}
      handleClose={onClose}
      allowBackgroundClose={onClose}
      showActions={false}
      title={
        isStillbirth
          ? 'Add the stillborn child to the pedigree'
          : 'Add the child to the pedigree'
      }
      minHeight={160}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Typography variant="body2">
          {isStillbirth
            ? 'This pregnancy is recorded as a stillbirth. Add the child to the pedigree as a person, marked deceased?'
            : 'This pregnancy is recorded as a live birth. Add the child to the pedigree?'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {known
            ? `The child will be added as ${
                gender === GENDER.male ? 'male' : 'female'
              }, from the sex recorded on the fetus.`
            : 'No sex is recorded on the fetus, so the child will be added with sex unknown. Set the sex on the fetus first if it is known.'}
        </Typography>
        {isStillbirth && (
          <Typography variant="caption" color="text.secondary">
            {dob
              ? `Recorded as deceased, with ${dob} as both the date of birth and the date of death. A "${'SB'}" note is added so it can be labelled on the pedigree.`
              : 'Recorded as deceased with no date, since none is recorded on the fetus. A "SB" note is added so it can be labelled on the pedigree.'}
          </Typography>
        )}
        {missing.length > 0 && (
          <Typography variant="caption" color="warning.main">
            No {missing.join(' or ')} recorded on the fetus — you can still
            continue, and add {missing.length > 1 ? 'them' : 'it'} to the person
            afterwards.
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="secondary"
            disabled={busy}
            onClick={onConfirm}
          >
            {isStillbirth ? 'Yes, add the person' : 'Yes, add the child'}
          </Button>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            Not now
          </Button>
        </Box>
      </Box>
    </CustomDialog>
  );
}

// Per-fetus DIAGNOSES (REQ-E2, decision 20). Stored in the fetus's own `diagnoses` array using the
// same embedded schema a person carries, which is what lets the existing shading pass consume them and
// makes REQ-I5's carry-over on birth a plain array copy.
//
// An Autocomplete is safe here even though this dialog opens from the context menu's
// ClickAwayListener: its listbox is a Popper that is a React descendant, so a click inside it counts
// as "inside" — the same reason the MUI X DatePicker in this form works. A `Select` would NOT be safe
// (Menu → Popover → Modal), which is why the affected-status style fields elsewhere use RadioGroups.
function FetusDiagnoses({
  diagnoses,
  phenotypes,
  familyDiagnoses,
  recordedBy,
  onChange,
  onPhenotypesChange
}) {
  const [options, setOptions] = React.useState([]);
  const [term, setTerm] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const list = Array.isArray(diagnoses) ? diagnoses : [];
  const features = Array.isArray(phenotypes) ? phenotypes : [];

  React.useEffect(() => {
    const q = term.trim();
    if (q.length < 3) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      axios
        .get(getHPOSearchURL(q, 1, HPO_CONSTANTS.category.disease))
        .then((res) => {
          if (cancelled) return;
          // The search returns diseases, genes and HPO features mixed. Genes are dropped — a fetus
          // record has no genotype field — but HPO terms are KEPT and routed to `phenotypes` on
          // selection, which is how the app's own diagnoses menu behaves: one search box, split by id
          // prefix. Two separate searches would make the clinician guess which box a term lives in.
          setOptions(
            (res.data?.results || []).filter(
              (r) => r?.id && !isGeneTermId(String(r.id))
            )
          );
        })
        .catch(() => !cancelled && setOptions([]))
        .finally(() => !cancelled && setLoading(false));
    }, 300); // debounce — the search endpoint is hit per keystroke otherwise
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term]);

  const add = (term_) => {
    if (!term_?.id) return;
    const done = () => {
      setTerm('');
      setOptions([]);
    };
    // An HPO term is a clinical FEATURE, not a diagnosis — different array, different render pass
    // (a label beside the symbol rather than shading).
    if (isPhenotypeTermId(String(term_.id))) {
      if (features.some((p) => String(p.id) === String(term_.id)))
        return done();
      onPhenotypesChange?.([
        ...features,
        buildFetusPhenotype(term_, { recordedBy })
      ]);
      return done();
    }
    if (list.some((d) => String(d.id) === String(term_.id))) return done();
    onChange([
      ...list,
      buildFetusDiagnosis(term_, { familyDiagnoses, recordedBy })
    ]);
    return done();
  };

  return (
    <Box>
      <FormLabel sx={{ fontSize: 12 }}>
        Diagnoses &amp; clinical features
      </FormLabel>
      <Autocomplete
        size="small"
        options={options}
        loading={loading}
        filterOptions={(x) => x} // the server already filtered; don't filter twice
        getOptionLabel={(o) => (o?.name ? `${o.name} (${o.id})` : '')}
        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
        inputValue={term}
        onInputChange={(_e, v) => setTerm(v)}
        value={null}
        onChange={(_e, v) => add(v)}
        noOptionsText={
          term.trim().length < 3 ? 'Type to search…' : 'No matches'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Search a condition…"
          />
        )}
      />
      {list.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
          {list.map((d) => (
            <Chip
              key={String(d.id)}
              size="small"
              label={d.name || d.id}
              onDelete={() =>
                onChange(list.filter((x) => String(x.id) !== String(d.id)))
              }
              // The colour the symbol will shade with, so the clinician can see it before saving.
              sx={{
                borderLeft: `4px solid ${d.color || '#999'}`,
                borderRadius: '4px'
              }}
            />
          ))}
          {/* Features are visually distinct from diagnoses: they print as a LABEL beside the symbol
              rather than shading it, so showing them identically would misrepresent what will happen
              on the chart. */}
          {features.map((p) => (
            <Chip
              key={String(p.id)}
              size="small"
              variant="outlined"
              label={p.name || p.id}
              onDelete={() =>
                onPhenotypesChange?.(
                  features.filter((x) => String(x.id) !== String(p.id))
                )
              }
              sx={{ borderRadius: '4px', borderStyle: 'dashed' }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// A stored donor reference (`donors[].memberId` / `gameteDonors[].memberId`) can be a member's _id,
// uid or pid — whichever was current when it was written. A candidate row exposes ONE of those as
// its `ref`, so matching has to consider all three the way liveBirthInheritance.matchesMemberRef
// does, or a reference written before a save stops matching the same person after it. Exported for
// its own test.
export const refMatchesCandidate = (ref, candidate) =>
  ref != null &&
  candidate != null &&
  [candidate.ref, candidate._id, candidate.uid, candidate.pid].some(
    (v) => v != null && String(v) === String(ref)
  );

// Who a gamete donor is (TGCLOUD-1597). Three states, and anonymous stays the default: a donor is
// most often genuinely unknown, and nothing should push a clinician into naming one.
//
// Picking an existing family member is the common case Kathryn described — a sister donating an egg,
// a brother-in-law donating sperm. "A new person" covers the unrelated donor: they become a full
// member with their own UPN, so their own family history can then be recorded as normal, which is
// the other half of the ticket.
//
// Inline RadioGroup, NOT an MUI Select — a portalled dropdown trips the context-menu
// ClickAwayListener and closes the whole modal (the same trap documented on the modal itself).
export function DonorIdentityPicker({
  role,
  label,
  memberId,
  onChange,
  candidates,
  onCreateDonorPerson,
  disabled,
  // TGCLOUD-1613 'donated' direction: the prompt reads the other way round, and there is nobody to
  // be anonymous — the person being recorded IS the donor, so the other end must be a named
  // individual. Both default to the original recipient-first behaviour.
  promptOverride = null,
  allowAnonymous = true
}) {
  const [creating, setCreating] = React.useState(false);
  // Matched with the SAME three-way member match the render derivation uses (_id | uid | pid), not
  // string equality against one chosen ref.
  //
  // This matters for a donor added via "Add a new person as this donor": that person has only a uid
  // until the pedigree saves, so `memberId` is their uid — but once saved they have a server `_id`,
  // and a candidate list keyed on `_id` would then fail to match the stored uid. The donor line kept
  // drawing (the derivation matches all three), but reopening the editor showed the donor as "no
  // longer in this family" and a save from that state would have silently dropped the link.
  const selected = candidates.find((c) => refMatchesCandidate(memberId, c));
  // A named donor who genuinely is no longer in the family (deleted, or merged away) must be visible
  // rather than silently reverting to "anonymous" — the clinician needs to see the link is broken.
  const danglingRef = memberId != null && !selected;

  const createNow = async () => {
    if (!onCreateDonorPerson) return;
    setCreating(true);
    try {
      const ref = await onCreateDonorPerson(role);
      if (ref != null) onChange(String(ref));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ pl: 3.5, pb: 1 }}>
      <FormLabel sx={{ fontSize: '0.78rem' }}>
        {promptOverride || `${label} — who was it?`}
      </FormLabel>
      <RadioGroup
        value={memberId == null ? '' : String(memberId)}
        onChange={(e) =>
          onChange(e.target.value === '' ? null : e.target.value)
        }
      >
        {allowAnonymous && (
          <FormControlLabel
            value=""
            disabled={disabled}
            control={<Radio size="small" />}
            label="Anonymous / not known"
          />
        )}
        {candidates.map((c) => (
          <FormControlLabel
            key={String(c.ref)}
            value={String(c.ref)}
            disabled={disabled}
            control={<Radio size="small" />}
            label={c.label}
          />
        ))}
        {danglingRef && (
          <FormControlLabel
            value={String(memberId)}
            disabled={disabled}
            control={<Radio size="small" />}
            label="Previously recorded person (no longer in this family)"
          />
        )}
      </RadioGroup>
      {onCreateDonorPerson && (
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || creating}
          onClick={createNow}
          sx={{ mt: 0.5 }}
        >
          {creating ? 'Adding…' : 'Add a new person as this donor'}
        </Button>
      )}
    </Box>
  );
}

// Clinician pregnancy form (add + edit). Inline RadioGroups — NOT MUI Selects, whose
// portalled dropdown trips the context-menu ClickAwayListener and closes the dialog.
export function PregnancyModal({
  record,
  spouseOptions = [],
  // The family's canonical diagnoses, so a condition already recorded on a relative keeps its colour
  // on the fetus (decision 20), and the clinician's name for `recordedBy`.
  familyDiagnoses = [],
  recordedBy,
  // Ask the host to open a prenatal clinical record scoped to one fetus (decision 21). The note form
  // lives in the Clinical Records sidebar section, so the modal cannot open it directly — it hands the
  // scope up and closes.
  onAddFetusEvent,
  // The person carrying this pregnancy, for the REQ-E3 person+fetus summary. Optional: a host that
  // does not pass it simply gets no summary line.
  carrier,
  onSubmit,
  onClose,
  onDelete,
  onConvert,
  historyAction,
  // TGCLOUD-1597 — every member of the family, so a donor can be picked from people already on the
  // pedigree. Optional: a host that does not pass it gets the pre-1597 anonymous-only donor UI.
  memberRecords = [],
  // Creates a NEW unrelated person to be the donor and resolves to their member reference. Supplied
  // by the host because it has to touch member state; absent → that option is not offered.
  onCreateDonorPerson,
  // Which spouse a NEW record should default "conceived with" to (TGCLOUD-1617 item 3). The Add
  // Relative panel's Add fetus launcher sits inside the child-scope container and passes whichever
  // spouse that container has selected, so the launcher adds a not-yet-born child to the same union
  // the Son and Daughter tiles above it would. Hosts that omit it keep the previous default — the
  // first spouse — and an existing record always seeds from its own stored value.
  initialSpouseRef = null
}) {
  const preg = record || {};
  const [chooserOpen, setChooserOpen] = React.useState(false);
  const [converting, setConverting] = React.useState(false);
  // The record the sex-chooser will convert — the just-saved one (save prompt) or the
  // existing one (persistent callout).
  const [pendingRecord, setPendingRecord] = React.useState(null);
  // Which fetus the sex-chooser is about to convert.
  const [pendingFetusIndex, setPendingFetusIndex] = React.useState(0);
  // FETUSES — a pregnancy holds many (multiple gestation). Seeded through `normalizeFetuses`, so a
  // record written before fetuses existed opens as exactly ONE fetus pre-filled from its legacy
  // top-level fields: editing an old pregnancy looks and behaves exactly as it did.
  const [fetuses, setFetuses] = React.useState(() => {
    const seeded = normalizeFetuses(preg).map((f) => ({
      name: f.name || '',
      outcome: f.outcome || PREGNANCY_OUTCOME.ongoing,
      fetalSexKnown: !!f.fetalSexKnown,
      gender:
        f.gender && f.gender !== GENDER.unknown ? f.gender : GENDER.female,
      sexDeterminationMethod: f.sexDeterminationMethod || '',
      karyotype: f.karyotype || '',
      pedigreeSymbolText: f.pedigreeSymbolText || '',
      dob: toDateValue(f.dob),
      dateAborted: toDateValue(f.dateAborted),
      hideInPedigree: !!f.hideInPedigree,
      comment: f.comment || '',
      // Carried through untouched so editing a pregnancy cannot orphan a born child or drop a
      // fetus's clinical data.
      bornMemberId: f.bornMemberId,
      diagnoses: f.diagnoses,
      phenotypes: f.phenotypes,
      _id: f._id
    }));
    return seeded.length ? seeded : [blankFetus()];
  });
  const setFetusField = (index, patch) =>
    setFetuses((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...patch } : f))
    );
  const addFetus = () => setFetuses((prev) => [...prev, blankFetus()]);
  const removeFetus = (index) =>
    setFetuses((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)
    );

  const [weeks, setWeeks] = React.useState(preg.gestationalWeeks ?? '');
  // Two-axis conception model (partner × donor(s)); never assumes a donor. Seed from the stored
  // record; for a NEW record default the partner to the carrier's spouse (if any), donors OFF.
  const axes = conceptionToAxes(preg);
  const validSpouseRef = spouseOptions.some(
    (o) => String(o.ref) === String(axes.spouseRef)
  )
    ? axes.spouseRef
    : null;
  const [spouseRef, setSpouseRef] = React.useState(
    validSpouseRef ??
      (record
        ? null
        : (spouseOptions.some((o) => String(o.ref) === String(initialSpouseRef))
            ? initialSpouseRef
            : null) ??
          spouseOptions[0]?.ref ??
          null)
  );
  const [spermDonor, setSpermDonor] = React.useState(axes.spermDonor);
  const [eggDonor, setEggDonor] = React.useState(axes.eggDonor);
  // TGCLOUD-1597 — WHO the donor is. null = anonymous, which is the default and what every record
  // written before this ticket recovers as.
  const [spermDonorMemberId, setSpermDonorMemberId] = React.useState(
    axes.spermDonorMemberId
  );
  const [eggDonorMemberId, setEggDonorMemberId] = React.useState(
    axes.eggDonorMemberId
  );
  const [zygosity, setZygosity] = React.useState(preg.zygosity || 'unknown');
  const [note, setNote] = React.useState(preg.comments || '');
  const [hidden, setHidden] = React.useState(!!preg.hidden);

  // ── Obstetric record (REQ-B3). All optional — a pregnancy is valid with nothing filled in, and
  // no calculation ever blocks a save (REQ-C3). Native date inputs on purpose: an MUI date picker
  // portals its popup to document.body, which the context-menu ClickAwayListener reads as a
  // click-away and closes this whole modal (the same trap that rules out Selects here).
  const [dateOfRecord, setDateOfRecord] = React.useState(
    toDateValue(preg.dateOfRecord)
  );
  const [lmp, setLmp] = React.useState(toDateValue(preg.lastMenstrualDate));
  const [scanDate, setScanDate] = React.useState(
    toDateValue(preg.initialScanDate)
  );
  const [scanWeeks, setScanWeeks] = React.useState(
    preg.gestationAtScanWeeks ?? ''
  );
  const [scanDays, setScanDays] = React.useState(
    preg.gestationAtScanDays ?? ''
  );
  const [edd, setEdd] = React.useState(toDateValue(preg.edd));
  const [completed, setCompleted] = React.useState(!!preg.completed);
  const [completionDate, setCompletionDate] = React.useState(
    toDateValue(preg.completionDate)
  );
  const [staffMember, setStaffMember] = React.useState(preg.staffMember || '');
  const [serviceProvider, setServiceProvider] = React.useState(
    preg.serviceProvider || ''
  );
  const [obstetrician, setObstetrician] = React.useState(
    preg.obstetrician || ''
  );
  const [otherDoctor, setOtherDoctor] = React.useState(preg.otherDoctor || '');
  const [prenatalComment, setPrenatalComment] = React.useState(
    preg.prenatalComment || ''
  );

  // Recomputed on every render, so the read-only values track the inputs live without a save
  // (REQ-C4). Placeholder-filled, so a missing input explains itself rather than showing a bogus 0.
  const obstetrics = deriveObstetrics({
    lastMenstrualDate: lmp || undefined,
    initialScanDate: scanDate || undefined,
    gestationAtScanWeeks: scanWeeks,
    gestationAtScanDays: scanDays
  });

  // Live birth (Bennett) is a passive outcome we keep on the record; the person is added as a
  // deliberate step. Offered for a SINGLE-fetus pregnancy whose fetus is a live birth with no child
  // linked yet. Deliberately NOT offered for a multiple: the conversion creates one child from
  // "the record", so it needs a per-fetus target before it can be correct for twins — that comes
  // with the birth-transition work (Phase 5). Better to withhold it than to silently add one child
  // for a twin delivery.
  // PER FETUS (Tim's decision 18). Each live-born fetus is converted on its own, so one twin can be
  // born while the other is a stillbirth or loss — and the co-twin's record is never touched. The old
  // rule withheld the whole thing for a multiple because the conversion could only create one child
  // "from the record".
  // See CONVERTIBLE_OUTCOMES / canOfferConversion at module scope — hoisted so TGCLOUD-1629's
  // "an existing stillbirth is offered the conversion when reopened" is provable by test.
  const eligibleFetusIndexes = convertibleFetusIndexes(fetuses);
  const canOfferLiveBirth = canOfferConversion({
    fetuses,
    record: preg,
    hasHandler: !!onConvert
  });

  // The carrying person, for the REQ-E3 summary. `carrier` is passed by the hosts; nothing is fetched
  // here, so the summary is silent rather than wrong when it is unavailable.
  const carrierSummary = React.useMemo(() => {
    if (!carrier) return null;
    const name =
      [carrier.firstName, carrier.lastName].filter(Boolean).join(' ') ||
      (carrier.pid != null ? `Person ${carrier.pid}` : '');
    if (!name) return null;
    const detail = [
      carrier.familyMemberId || carrier.proBandId,
      carrier.dob ? `b. ${carrier.dob}` : null
    ]
      .filter(Boolean)
      .join(' · ');
    return { name, detail };
  }, [carrier]);

  // What this pregnancy amounts to, in one line: how many fetuses, how far along, and how many of them
  // carry clinical findings — the count is what tells a clinician whether there is anything to read.
  const fetusSummaryText = React.useMemo(() => {
    const withFindings = fetuses.filter(
      (f) =>
        (f.diagnoses || []).length ||
        (f.phenotypes || []).length ||
        (f.karyotype || '').trim()
    ).length;
    return [
      fetuses.length === 1 ? '1 fetus' : `${fetuses.length} fetuses`,
      weeks !== '' && weeks != null ? `${weeks} weeks` : null,
      withFindings ? `${withFindings} with findings` : null
    ]
      .filter(Boolean)
      .join(' · ');
  }, [fetuses, weeks]);

  const buildPayload = () => {
    const weeksNum =
      weeks === '' || weeks == null || Number.isNaN(Number(weeks))
        ? undefined
        : Number(weeks);
    const conception = deriveConception({
      spouseRef,
      spermDonor,
      eggDonor,
      // Cleared when the role is unticked, so an untick can never leave a stale identity behind.
      spermDonorMemberId: spermDonor ? spermDonorMemberId : null,
      eggDonorMemberId: eggDonor ? eggDonorMemberId : null
    });
    const num = (v) =>
      v === '' || v == null || Number.isNaN(Number(v)) ? undefined : Number(v);
    const cleanFetuses = fetuses.map((f) => ({
      ...(f._id ? { _id: f._id } : {}),
      name: f.name || undefined,
      outcome: f.outcome,
      fetalSexKnown: f.fetalSexKnown,
      gender: f.fetalSexKnown ? f.gender : GENDER.unknown,
      sexDeterminationMethod: f.sexDeterminationMethod || undefined,
      karyotype: f.karyotype || undefined,
      pedigreeSymbolText: f.pedigreeSymbolText || undefined,
      dob: f.dob || undefined,
      dateAborted: f.dateAborted || undefined,
      hideInPedigree: f.hideInPedigree,
      comment: f.comment || undefined,
      bornMemberId: f.bornMemberId,
      diagnoses: f.diagnoses,
      phenotypes: f.phenotypes
    }));
    // The first fetus is ALSO mirrored onto the legacy top-level fields. Those are still read by
    // consumers that predate fetuses — the FHQ tables and the PDF (`outcome`, delivery-mode
    // gating), the XML serializer, Browse All Records, and the record-history audit config — so
    // dropping them would silently blank a pregnancy in all of those. `fetuses` is the source of
    // truth for the pedigree; these stay in sync behind it.
    const first = cleanFetuses[0] || {};
    return {
      fetuses: cleanFetuses,
      outcome: first.outcome,
      gestationalWeeks: weeksNum,
      fetalSexKnown: first.fetalSexKnown,
      fetalSex: first.fetalSexKnown ? first.gender : undefined,
      sexDeterminationMethod: first.sexDeterminationMethod,
      karyotype: first.karyotype,
      multiplicity: cleanFetuses.length > 1 ? cleanFetuses.length : undefined,
      // Only meaningful for a multiple; dropped for a singleton so a stale value cannot linger.
      zygosity: cleanFetuses.length > 1 ? zygosity : undefined,
      comments: note || undefined,
      hidden,
      conceivedWith: conception.conceivedWith,
      donors: conception.donors,
      otherParentMemberId: conception.otherParentMemberId,
      // Obstetric record. Empty strings are sent as undefined so a cleared field is dropped
      // rather than stored as '' (the update replaces the whole document).
      dateOfRecord: dateOfRecord || undefined,
      lastMenstrualDate: lmp || undefined,
      initialScanDate: scanDate || undefined,
      gestationAtScanWeeks: num(scanWeeks),
      gestationAtScanDays: num(scanDays),
      edd: edd || undefined,
      completed,
      completionDate: completionDate || undefined,
      staffMember: staffMember || undefined,
      serviceProvider: serviceProvider || undefined,
      obstetrician: obstetrician || undefined,
      otherDoctor: otherDoctor || undefined,
      prenatalComment: prenatalComment || undefined,
      source: 'pedigree'
    };
  };

  // Biological check: a named non-male partner needs a sperm donor (the carrier is female).
  const selectedSpouse = spouseOptions.find(
    (o) => String(o.ref) === String(spouseRef)
  );
  const conceptionError = conceptionValidationError({
    partnerSpecified: spouseRef != null,
    partnerGender: selectedSpouse?.gender ?? null,
    spermDonor
  });

  // TGCLOUD-1597 — who can be named as a donor: anyone in the family except the carrier, who
  // cannot donate to a pregnancy they are gestating. `ref` matches what donors[].memberId stores
  // and what deriveReproNodes resolves through findByMemberId.
  const carrierRef = carrier?._id || carrier?.pid;
  const donorCandidates = React.useMemo(
    () =>
      (memberRecords || [])
        .filter((m) => m && m.pid !== carrier?.pid)
        .map((m) => ({
          // `ref` is what gets WRITTEN (prefer the stable uid, which survives the pre-save window);
          // _id/uid/pid are all carried so refMatchesCandidate can recognise a reference written
          // under any of them.
          ref: m.uid || m._id || m.pid,
          _id: m._id,
          uid: m.uid,
          pid: m.pid,
          gender: m.gender,
          label:
            [m.firstName, m.lastName].filter(Boolean).join(' ') ||
            `Person ${m.pid}`
        })),
    [memberRecords, carrier]
  );
  const donorSexOf = React.useCallback(
    (ref) =>
      donorCandidates.find((c) => String(c.ref) === String(ref))?.gender ??
      null,
    [donorCandidates]
  );
  const donorError = donorIdentityValidationError({
    carrierRef,
    spermDonorMemberId: spermDonor ? spermDonorMemberId : null,
    eggDonorMemberId: eggDonor ? eggDonorMemberId : null
  });
  // Non-blocking: the gamete does not match the donor's recorded sex. Said out loud because it is
  // usually a mis-pick, but never refused — a trans donor or an unrecorded sex is legitimate.
  const donorWarning = donorSexWarning({
    spermDonorMemberId: spermDonor ? spermDonorMemberId : null,
    eggDonorMemberId: eggDonor ? eggDonorMemberId : null,
    sexOf: donorSexOf
  });

  // Save persists the record; ModalButtons awaits this for its loading state. onSubmit returns
  // the saved record (or false). On a live-birth record with no linked child, prompt to add
  // the son/daughter instead of closing.
  const handleSave = async () => {
    if (conceptionError) return; // block until the sperm-source rule is satisfied
    if (donorError) return; // and until the donor identity is a state that can be drawn
    const saved = await onSubmit(buildPayload());
    if (!saved) return; // stay open on failure
    // Auto-prompt only when there is exactly ONE fetus to add, so the flow stays unambiguous. With
    // several live-born fetuses the clinician converts each from its own panel instead — prompting in
    // sequence would obscure which baby the sex question is about.
    const savedEligible = normalizeFetuses(saved)
      .map((f, i) => (bornEligible(f) ? i : -1))
      .filter((i) => i !== -1);
    if (onConvert && savedEligible.length === 1) {
      setPendingRecord(saved);
      setPendingFetusIndex(savedEligible[0]);
      setChooserOpen(true);
    } else {
      onClose?.();
    }
  };

  // The fetus the prompt is about: from the record being converted where there is one (the just-saved
  // values are already in it), else from what is on screen — so the sex shown always matches what the
  // user sees. `newbornDefaultGender` returns null unless a sex was actually determined, and that maps
  // to `unknown`: the child is still added (as a diamond), it just asserts no sex.
  const pendingFetus = pendingRecord
    ? normalizeFetuses(pendingRecord)[pendingFetusIndex]
    : fetuses[pendingFetusIndex];
  const newbornGender = newbornDefaultGender(pendingFetus) ?? GENDER.unknown;
  // TGCLOUD-1616 — which conversion the prompt is about, so it can use the right words and warn
  // about a missing date. Read from the fetus, which is the only place the outcome lives per-fetus.
  const pendingIsStillbirth =
    pendingFetus?.outcome === PREGNANCY_OUTCOME.stillbirth;
  const pendingDob = pendingFetus?.dob || null;

  // Add the child for a saved live-birth record (from the save-prompt or the persistent callout).
  // Convert, then close.
  const handleConfirmAddChild = async () => {
    setConverting(true);
    const ok = await onConvert?.(
      pendingRecord,
      newbornGender,
      pendingFetusIndex
    );
    setConverting(false);
    setChooserOpen(false);
    if (ok !== false) onClose?.();
  };

  if (chooserOpen) {
    return (
      <LiveBirthConfirm
        onConfirm={handleConfirmAddChild}
        onClose={() => {
          setChooserOpen(false);
          onClose?.();
        }}
        busy={converting}
        gender={newbornGender}
        isStillbirth={pendingIsStillbirth}
        dob={pendingDob}
      />
    );
  }

  return (
    <CustomDialog
      open={true}
      handleClose={onClose}
      allowBackgroundClose={onClose}
      showActions={false}
      title={record ? 'Edit pregnancy' : 'Add pregnancy'}
      minHeight={320}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {/* Outcome now lives PER FETUS (below) — one twin can be ongoing while the other was
            lost, so a single pregnancy-level outcome cannot express it. */}

        {/* Persistent live-birth callout: a saved live-birth record with no child yet. */}
        {canOfferLiveBirth && record?._id && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover'
            }}
          >
            <Typography variant="body2">
              {calloutText(eligibleFetusIndexes, fetuses)}
            </Typography>
            {eligibleFetusIndexes.map((idx) => (
              <Button
                key={idx}
                variant="contained"
                color="secondary"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  setPendingRecord(record);
                  setPendingFetusIndex(idx);
                  setChooserOpen(true);
                }}
              >
                {fetuses.length > 1
                  ? `Add child for ${
                      fetuses[idx]?.name ||
                      fetuses[idx]?.pedigreeSymbolText ||
                      `fetus ${idx + 1}`
                    }`
                  : 'Add the child to the pedigree'}
              </Button>
            ))}
          </Box>
        )}

        <FormControl>
          <FormLabel sx={{ fontSize: '0.85rem' }}>
            Conceived with a partner
          </FormLabel>
          <RadioGroup
            value={spouseRef == null ? '' : String(spouseRef)}
            onChange={(e) =>
              setSpouseRef(e.target.value === '' ? null : e.target.value)
            }
          >
            <FormControlLabel
              value=""
              control={<Radio size="small" />}
              label="No partner / not specified"
            />
            {spouseOptions.map((o) => (
              <FormControlLabel
                key={String(o.ref)}
                value={String(o.ref)}
                control={<Radio size="small" />}
                label={o.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl>
          <FormLabel sx={{ fontSize: '0.85rem' }}>
            Gamete donor(s) — leave unticked unless a donor was used
          </FormLabel>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={spermDonor}
                onChange={(e) => setSpermDonor(e.target.checked)}
              />
            }
            label="Sperm donor"
          />
          {spermDonor && (
            <DonorIdentityPicker
              role="sperm"
              label="Sperm donor"
              memberId={spermDonorMemberId}
              onChange={setSpermDonorMemberId}
              candidates={donorCandidates}
              onCreateDonorPerson={onCreateDonorPerson}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={eggDonor}
                onChange={(e) => setEggDonor(e.target.checked)}
              />
            }
            label="Egg donor"
          />
          {eggDonor && (
            <DonorIdentityPicker
              role="ovum"
              label="Egg donor"
              memberId={eggDonorMemberId}
              onChange={setEggDonorMemberId}
              candidates={donorCandidates}
              onCreateDonorPerson={onCreateDonorPerson}
            />
          )}
          {conceptionError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {conceptionError}
            </Typography>
          )}
          {donorError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {donorError}
            </Typography>
          )}
          {!donorError && donorWarning && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
              {donorWarning}
            </Typography>
          )}
        </FormControl>

        <TextInput
          type="number"
          label="Gestational age (weeks)"
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 45 }}
          value={weeks}
          onChange={(e) => setWeeks(e.target.value)}
        />

        {/* ── Fetuses ───────────────────────────────────────────────────────────────
            A pregnancy holds many (multiple gestation, REQ-D1/B6). Each fetus carries its OWN
            outcome — so one twin can be ongoing while the other was lost — and its own sex,
            karyotype, symbol text and hide flag. Two or more fetuses draw as twins on the
            pedigree. A single-fetus pregnancy reads just as it did before fetuses existed. */}
        <Divider sx={{ mt: 1 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap'
          }}
        >
          <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 600, flexGrow: 1 }}>
            {fetuses.length > 1
              ? `Fetuses (${fetuses.length}) — drawn as ${
                  fetuses.length === 2 ? 'twins' : 'a multiple birth'
                }`
              : 'Fetus'}
          </FormLabel>
          <Button size="small" variant="outlined" onClick={addFetus}>
            Add fetus
          </Button>
        </Box>

        {/* ZYGOSITY — a property of the multiple, not of one fetus, so it sits above the fetus
            cards. Bennett draws a horizontal bar between monozygotic twins, plain slanted lines for
            dizygotic, and a "?" when zygosity is unknown; only shown once there IS a multiple. */}
        {fetuses.length > 1 && (
          <FormControl>
            <FormLabel sx={{ fontSize: '0.85rem' }}>Zygosity</FormLabel>
            <RadioGroup
              row
              value={zygosity}
              onChange={(e) => setZygosity(e.target.value)}
            >
              <FormControlLabel
                value="unknown"
                control={<Radio size="small" />}
                label='Unknown ("?")'
              />
              <FormControlLabel
                value="monozygotic"
                control={<Radio size="small" />}
                label="Monozygotic"
              />
              <FormControlLabel
                value="dizygotic"
                control={<Radio size="small" />}
                label="Dizygotic"
              />
            </RadioGroup>
          </FormControl>
        )}

        {/* PERSON + FETUS SUMMARY (REQ-E3). A clinician working on a fetus needs the carrying person in
            view: the fetus has no identity of its own, so "whose pregnancy is this, and how far along"
            is the context that makes the rest of the form interpretable. Read-only, and derived — it
            states what is already recorded rather than adding a place to record it. */}
        {carrierSummary && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: 0.75,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: 'action.hover'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {carrierSummary.name}
            </Typography>
            {carrierSummary.detail && (
              <Typography variant="caption" color="text.secondary">
                {carrierSummary.detail}
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {fetusSummaryText}
            </Typography>
          </Box>
        )}

        {fetuses.map((f, i) => (
          <Box
            key={f._id || i}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {fetuses.length > 1 ? `Fetus ${i + 1}` : 'Fetus details'}
                {f.bornMemberId ? ' · born' : ''}
              </Typography>
              {fetuses.length > 1 && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeFetus(i)}
                >
                  Remove
                </Button>
              )}
            </Box>

            <FormControl>
              <FormLabel sx={{ fontSize: '0.85rem' }}>Outcome</FormLabel>
              <RadioGroup
                value={f.outcome}
                onChange={(e) => setFetusField(i, { outcome: e.target.value })}
              >
                {OUTCOME_LABELS.map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio size="small" />}
                    label={label}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <TextInput
              type="text"
              label="Name (optional — often unknown)"
              variant="outlined"
              size="small"
              fullWidth
              value={f.name}
              onChange={(e) => setFetusField(i, { name: e.target.value })}
            />

            {/* SEX (TGCLOUD-1617 item 8). For a LIVE BIRTH the "fetal sex known" checkbox is
                redundant — Kathryn: "if we click live birth… that should automatically just give the
                gender fields available" — so the outcome exposes the sex directly, with Unknown kept as
                a real option because, in her words, "we do have disorders of gender, so it won't always
                be female or male". For any other outcome the checkbox stays: mid-pregnancy the sex
                genuinely may be unobserved, and "not looked for" is different from "unknown".
                Storage is unchanged either way — see buildPayload. */}
            {f.outcome === PREGNANCY_OUTCOME.liveBirth ? (
              <FormControl>
                <FormLabel sx={{ fontSize: '0.85rem' }}>Sex</FormLabel>
                <RadioGroup
                  row
                  value={f.fetalSexKnown ? f.gender : GENDER.unknown}
                  onChange={(e) =>
                    setFetusField(i, {
                      gender: e.target.value,
                      // Unknown is recorded as "not known" rather than as an asserted sex, which is
                      // what the payload builder and the symbol shape both already expect.
                      fetalSexKnown: e.target.value !== GENDER.unknown
                    })
                  }
                >
                  <FormControlLabel
                    value={GENDER.female}
                    control={<Radio size="small" />}
                    label="Female"
                  />
                  <FormControlLabel
                    value={GENDER.male}
                    control={<Radio size="small" />}
                    label="Male"
                  />
                  <FormControlLabel
                    value={GENDER.unknown}
                    control={<Radio size="small" />}
                    label="Unknown"
                  />
                </RadioGroup>
              </FormControl>
            ) : (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={f.fetalSexKnown}
                    onChange={(e) =>
                      setFetusField(i, { fetalSexKnown: e.target.checked })
                    }
                  />
                }
                label="Fetal sex known"
              />
            )}
            {/* The prenatal two-way picker, only for a non-live-birth outcome — the live-birth case
                above already includes the sex (with Unknown) in one control. */}
            {f.fetalSexKnown && f.outcome !== PREGNANCY_OUTCOME.liveBirth && (
              <FormControl>
                <FormLabel sx={{ fontSize: '0.85rem' }}>Fetal sex</FormLabel>
                <RadioGroup
                  row
                  value={f.gender}
                  onChange={(e) => setFetusField(i, { gender: e.target.value })}
                >
                  <FormControlLabel
                    value={GENDER.female}
                    control={<Radio size="small" />}
                    label="Female"
                  />
                  <FormControlLabel
                    value={GENDER.male}
                    control={<Radio size="small" />}
                    label="Male"
                  />
                </RadioGroup>
              </FormControl>
            )}
            {/* Bennett annotates HOW the sex was determined ("Female by US"). Shown whenever a sex is
                recorded, INCLUDING a live birth: gating it on the outcome would leave an already-stored
                value visible on the chart but no longer editable anywhere. */}
            {f.fetalSexKnown && (
              <TextInput
                type="text"
                label="How was fetal sex determined? (e.g. US, NIPT)"
                variant="outlined"
                size="small"
                fullWidth
                value={f.sexDeterminationMethod}
                onChange={(e) =>
                  setFetusField(i, {
                    sexDeterminationMethod: e.target.value
                  })
                }
              />
            )}

            <TextInput
              type="text"
              label="Karyotype / finding (optional)"
              variant="outlined"
              size="small"
              fullWidth
              value={f.karyotype}
              onChange={(e) => setFetusField(i, { karyotype: e.target.value })}
            />

            <FetusDiagnoses
              diagnoses={f.diagnoses}
              phenotypes={f.phenotypes}
              familyDiagnoses={familyDiagnoses}
              recordedBy={recordedBy}
              onChange={(next) => setFetusField(i, { diagnoses: next })}
              onPhenotypesChange={(next) =>
                setFetusField(i, { phenotypes: next })
              }
            />

            {/* A prenatal TEST for this fetus (REQ-E1) — the karyotype/NIPT result that pairs with the
                field above. Needs both ids to exist, so it appears only for a SAVED pregnancy whose
                fetus has been persisted: a fetus still being added has no id to attach a record to. */}
            {onAddFetusEvent && record?._id && f._id && (
              <Box>
                <FormLabel sx={{ fontSize: 12 }}>
                  Add a prenatal record for this fetus
                </FormLabel>
                {/* A row of buttons rather than a Select: a Select renders Menu → Popover → Modal,
                    the one portalled popup this dialog cannot host (see the note above
                    PregnancyModal). Three types is few enough that buttons are also fewer clicks. */}
                <Box
                  sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}
                >
                  {PRENATAL_NOTE_TYPES.map((noteType) => (
                    <Button
                      key={noteType}
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        onAddFetusEvent({
                          pregnancyId: String(record._id),
                          fetusId: String(f._id),
                          noteType
                        })
                      }
                    >
                      {noteType}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            {/* Prints INSIDE the symbol, like a person's pedigree text — the way to number the
                fetuses of a twin pregnancy. */}
            <TextInput
              type="text"
              label="Pedigree symbol text (e.g. 1, 2)"
              variant="outlined"
              size="small"
              fullWidth
              value={f.pedigreeSymbolText}
              onChange={(e) =>
                setFetusField(i, { pedigreeSymbolText: e.target.value })
              }
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <DateField
                label="Date of birth"
                value={f.dob}
                onChange={(v) => setFetusField(i, { dob: v })}
              />
              <DateField
                label="Date aborted"
                value={f.dateAborted}
                onChange={(v) => setFetusField(i, { dateAborted: v })}
              />
            </Box>

            <TextInput
              type="text"
              label="Fetus comment (optional)"
              variant="outlined"
              size="small"
              fullWidth
              value={f.comment}
              onChange={(e) => setFetusField(i, { comment: e.target.value })}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={f.hideInPedigree}
                  onChange={(e) =>
                    setFetusField(i, { hideInPedigree: e.target.checked })
                  }
                />
              }
              label="Hide this fetus on the pedigree"
            />
          </Box>
        ))}

        <TextInput
          type="text"
          label="Note (optional)"
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={hidden}
              onChange={(e) => setHidden(e.target.checked)}
            />
          }
          label="Hidden — kept as history, not drawn on the pedigree"
        />

        {/* ── Obstetric record (REQ-B3/C1–C4) ──────────────────────────────────────
            Everything below is optional. Two independent dating routes (LMP, or a dating scan);
            each derives a read-only gestation + EDD live as you type, and says so when it cannot.
            An EDD typed straight in with no LMP is a perfectly valid pregnancy. */}
        <Divider sx={{ mt: 1 }} />
        <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
          Obstetric record (all optional)
        </FormLabel>

        <DateField
          label="Date of record"
          value={dateOfRecord}
          onChange={setDateOfRecord}
        />

        <DateField
          label="Last menstrual period"
          value={lmp}
          onChange={setLmp}
        />
        <DerivedRow
          label="LMP est. gestation"
          value={obstetrics.lmpGestationText}
        />
        <DerivedRow
          label="LMP EDD"
          value={fmtDerivedDate(obstetrics.lmpEddText)}
        />

        <DateField
          label="Initial scan date"
          value={scanDate}
          onChange={setScanDate}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextInput
            type="number"
            label="Gestation at scan (weeks)"
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ min: 0, max: 45 }}
            value={scanWeeks}
            onChange={(e) => setScanWeeks(e.target.value)}
          />
          <TextInput
            type="number"
            label="days"
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ min: 0, max: 6 }}
            value={scanDays}
            onChange={(e) => setScanDays(e.target.value)}
          />
        </Box>
        <DerivedRow
          label="Gestation by scan"
          value={obstetrics.scanGestationText}
        />
        <DerivedRow
          label="EDD by scan"
          value={fmtDerivedDate(obstetrics.scanEddText)}
        />

        <DateField label="EDD (as recorded)" value={edd} onChange={setEdd} />

        <FormControlLabel
          control={
            <Checkbox
              checked={completed}
              onChange={(e) => setCompleted(e.target.checked)}
            />
          }
          label="Completed"
        />
        {completed && (
          <DateField
            label="Completion date"
            value={completionDate}
            onChange={setCompletionDate}
          />
        )}

        <TextInput
          type="text"
          label="Staff member"
          variant="outlined"
          size="small"
          fullWidth
          value={staffMember}
          onChange={(e) => setStaffMember(e.target.value)}
        />
        <TextInput
          type="text"
          label="Service provider"
          variant="outlined"
          size="small"
          fullWidth
          value={serviceProvider}
          onChange={(e) => setServiceProvider(e.target.value)}
        />
        <TextInput
          type="text"
          label="Obstetrician"
          variant="outlined"
          size="small"
          fullWidth
          value={obstetrician}
          onChange={(e) => setObstetrician(e.target.value)}
        />
        <TextInput
          type="text"
          label="Other doctor"
          variant="outlined"
          size="small"
          fullWidth
          value={otherDoctor}
          onChange={(e) => setOtherDoctor(e.target.value)}
        />
        <TextInput
          type="text"
          label="Prenatal comment"
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={prenatalComment}
          onChange={(e) => setPrenatalComment(e.target.value)}
        />
      </Box>

      {/* Cancel / Delete / Save Changes — the shared clinical-record action bar. The sx pushes
          ModalButtons' own (inset) divider down off the buttons so they don't butt against it.

          STICKY (TGCLOUD-1617 item 4). Kathryn: "if there's no other details available to me, I don't
          want to have to scroll all the way down to the bottom to click save pregnancy… if we are just
          doing quick data entry." This form is long — obstetric record plus a panel per fetus — so the
          save sat below the fold on open.

          Pinned here rather than via CustomDialog: this modal passes `showActions={false}` and renders
          its OWN action bar inside the scrolling content, so the dialog's action row is not in play.
          Sticking this row keeps ONE save button (no duplicate-button ambiguity) and changes nothing
          for any other dialog — an even smaller blast radius than the opt-in dialog prop that was
          planned before I found this renders its own bar. `background.paper` is needed or the form
          scrolls visibly underneath it. */}
      <Grid
        container
        sx={{
          mt: 2,
          '& .MuiDivider-root': { mb: 2 },
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          backgroundColor: 'background.paper'
        }}
      >
        <ModalButtons
          isEditing={!!record}
          onClick={handleSave}
          handleClose={onClose}
          hideContinueAdding={true}
          intermediateBtnLabel="Delete"
          intermediateAction={record && onDelete ? onDelete : null}
          addLabel="Save Pregnancy"
        />
      </Grid>

      {/* Record-history icon inside the content (not CustomDialog's footerAction) so its divider
          is an INSET content-level divider matching the one above the buttons, rather than the
          full-width dialog frame border. */}
      {record && historyAction && (
        <>
          <Divider sx={{ mt: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <IconButton
              size="small"
              title="Record history"
              onClick={historyAction}
            >
              <HistoryIcon />
            </IconButton>
          </Box>
        </>
      )}
    </CustomDialog>
  );
}

// Destructive-action confirm for deleting a pregnancy record.
export function DeleteConfirm({ onConfirm, onClose, deleting }) {
  return (
    <CustomDialog
      open={true}
      handleClose={onClose}
      allowBackgroundClose={onClose}
      showActions={true}
      title={'Delete pregnancy'}
      titleAppearance={'warning'}
      saveBtnLabel={deleting ? 'Deleting…' : 'Confirm Delete'}
      minHeight={160}
      onSave={onConfirm}
      isParentHandlingLoading={true}
      isParentLoading={deleting}
    >
      {/* The wording has to match what the route actually does — no more, no less. Since Tim's H3
          ruling this destroys the CLINICAL record (the PregnancyChanged document), so it names the
          data that goes with it. It does NOT claim to erase a patient's questionnaire answer,
          because the Pregnancy base document is deliberately spared: see
          purgePregnancyClinicalRecord. The second line states that, phrased conditionally on
          purpose — `source` cannot be trusted to identify provenance in the UI, because the merged
          read is `{ ...base, ...changed }` and a clinician edit stamps `source: 'pedigree'` over the
          base's 'fhq'. */}
      <Typography>
        Permanently delete this pregnancy? Everything recorded against it goes
        too — every fetus, their diagnoses and comments, and any linked donors.
        This cannot be undone.
      </Typography>
      <Typography sx={{ mt: 1 }} color="text.secondary">
        If this pregnancy came from the patient&apos;s questionnaire, the answer
        they submitted is kept as their original record.
      </Typography>
      {/* Worded to fit BOTH surfaces this dialog opens from: the context menu calls it "Hide from
          pedigree", the editor has a "Hidden" checkbox. */}
      <Typography sx={{ mt: 1 }} color="text.secondary">
        To take it off the pedigree but keep the record, mark it{' '}
        <strong>Hidden</strong> instead.
      </Typography>
    </CustomDialog>
  );
}

// REQ-H2. Desktop's dialog states what is about to be created and how to edit it; this is that
// text, reworded for the web app (and for the two cases — new pregnancy vs extra fetus).
export function AddFetusConfirm({ onConfirm, onClose, saving, existing }) {
  return (
    <CustomDialog
      open={true}
      handleClose={onClose}
      allowBackgroundClose={onClose}
      showActions={true}
      title={existing ? 'Add another fetus' : 'Add fetus'}
      saveBtnLabel={saving ? 'Adding…' : 'Add'}
      minHeight={170}
      onSave={onConfirm}
      isParentHandlingLoading={true}
      isParentLoading={saving}
    >
      <Typography>
        {existing
          ? 'A second fetus will be added to this pregnancy, and the two will be drawn as twins. Zygosity is left unrecorded until you set it.'
          : 'A pregnancy will be recorded on this person, dated today, with one fetus of unknown sex. Nothing else is assumed — no partner or donor is set.'}
      </Typography>
      <Typography sx={{ mt: 1 }} color="text.secondary">
        Double-click the symbol on the pedigree to fill in the details.
      </Typography>
    </CustomDialog>
  );
}

// CRUD over a carrier's pregnancy records. Persists via the shared endpoints, then patches
// the carrier's member.pregnancies in state (render-only — the wholesale save strips
// `pregnancies`). submit/delete return true on success so the caller can close its UI.
export function usePregnancyCrud({
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  setSnack,
  user
}) {
  const [busy, setBusy] = React.useState(false);

  const markSaving = () =>
    setPedigreeState?.((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

  const spouseOf = (carrier) =>
    (memberRecords || []).find((m) =>
      (carrier?.spouseIds || []).includes(m.pid)
    );

  // TGCLOUD-1597 — create a NEW unrelated person to be a donor, and hand back the reference the
  // pregnancy record stores in `donors[].memberId`.
  //
  // This is the "donor gametes from an unrelated person" case in the ticket. Because they are an
  // ORDINARY member, everything the ticket asks for follows with no extra code: they get their own
  // UPN on save, their own person sidebar, and Add Relative works on them — which is what makes
  // "explore the family history of the donor via the pedigree" possible.
  //
  // They start with no relationships at all, so the layout seats them as their own disconnected
  // component. That is expected, and it is why the ticket also asks for donor proxies.
  const createDonorPerson = async (role) => {
    if (!user?._id) {
      setSnack?.({
        status: true,
        msg: 'Cannot add the donor right now — reopen the pedigree and try again',
        severity: 'warning'
      });
      return null;
    }
    const person = createPerson(
      donorRoleGender(role),
      user._id,
      memberRecords || [],
      '' // an unrelated donor has no relationship to the proband
    );
    setMemberRecords((prev) => [...(prev || []), person]);
    markSaving();
    // The uid, not the pid: it is stable across saves and becomes the FamilyChanged _id, which is
    // the same reference `bornMemberId` uses for a converted child.
    return person.uid;
  };

  const submitPregnancy = async (carrier, record, formValues) => {
    if (busy) return false;
    const carrierId = carrier?._id;
    if (!carrierId) {
      setSnack?.({
        status: true,
        msg: 'Save the pedigree first, then add a pregnancy',
        severity: 'warning'
      });
      return false;
    }
    const editing = !!record;
    // conceivedWith / donors / otherParentMemberId are already resolved by the shared
    // deriveConception helper in the form payload — persist them as-is (no recompute, so
    // partner+donor combos keep their otherParentMemberId).
    const values = { ...formValues };
    setBusy(true);
    try {
      const { data } = await axios.post(
        APICONSTANTS.probandPregnanciesDetails.replace(':user_id', carrierId),
        {
          formValues: editing ? { ...values, _id: record._id } : values,
          isEditing: editing
        }
      );
      const newId = data?._id || record?._id;
      const saved = editing
        ? { ...record, ...values }
        : { ...values, _id: newId };
      setMemberRecords((prev) =>
        prev.map((m) => {
          if (m.pid !== carrier.pid) return m;
          const pregs = m.pregnancies || [];
          if (editing) {
            return {
              ...m,
              pregnancies: pregs.map((p) =>
                String(p._id) === String(record._id) ? { ...p, ...values } : p
              )
            };
          }
          return { ...m, pregnancies: [...pregs, saved] };
        })
      );
      markSaving();
      setBusy(false);
      // Return the saved record (with _id) so the modal can offer live-birth conversion of a
      // freshly-created record, not just an existing one.
      return saved;
    } catch (err) {
      setSnack?.({
        status: true,
        msg: err?.response?.data?.message || 'Failed to save pregnancy',
        severity: 'error'
      });
      setBusy(false);
      return false;
    }
  };

  const deletePregnancy = async (carrier, record) => {
    if (busy || !carrier?._id || !record?._id) return false;
    setBusy(true);
    try {
      await axios.delete(
        APICONSTANTS.deleteProbandPregnancy
          .replace(':user_id', carrier._id)
          .replace(':pregnancy_id', String(record._id))
      );
      setMemberRecords((prev) =>
        prev.map((m) =>
          m.pid === carrier.pid
            ? {
                ...m,
                pregnancies: (m.pregnancies || []).filter(
                  (p) => String(p._id) !== String(record._id)
                )
              }
            : m
        )
      );
      markSaving();
      setBusy(false);
      return true;
    } catch (err) {
      setSnack?.({
        status: true,
        msg: err?.response?.data?.message || 'Failed to delete pregnancy',
        severity: 'error'
      });
      setBusy(false);
      return false;
    }
  };

  // Toggle a record's `hidden` flag. Sends the FULL record (hidden flipped) as an edit — the
  // update endpoint replaces the changed doc, so a partial { hidden } would drop other fields.
  // A hidden record stays in the Reproductive sidebar but deriveReproNodes stops drawing it.
  const toggleHidden = async (carrier, record) => {
    if (busy || !carrier?._id || !record?._id) return false;
    const next = !record.hidden;
    setBusy(true);
    try {
      await axios.post(
        APICONSTANTS.probandPregnanciesDetails.replace(':user_id', carrier._id),
        { formValues: { ...record, hidden: next }, isEditing: true }
      );
      setMemberRecords((prev) =>
        prev.map((m) =>
          m.pid === carrier.pid
            ? {
                ...m,
                pregnancies: (m.pregnancies || []).map((p) =>
                  String(p._id) === String(record._id)
                    ? { ...p, hidden: next }
                    : p
                )
              }
            : m
        )
      );
      markSaving();
      setBusy(false);
      return true;
    } catch (err) {
      setSnack?.({
        status: true,
        msg: err?.response?.data?.message || 'Failed to update pregnancy',
        severity: 'error'
      });
      setBusy(false);
      return false;
    }
  };

  // Fetus → person conversion (Bennett: a liveborn is an ordinary person node). Creates a real
  // child under the carrier's union (partner-conceived) or the carrier alone (donor-conceived),
  // links the record's `bornMemberId` to the child's stable uid, and settles the outcome. The
  // child persists on the next pedigree save (markSaving); the record update persists
  // immediately. `childGender` is 'male' | 'female' | 'unknown' (asked in the UI).
  //
  // TGCLOUD-1616 — this used to be `convertToLiveBirth` and hardcoded `outcome: liveBirth` in
  // three places. A STILLBIRTH now converts too, and it must keep `outcome: 'stillbirth'`: parity
  // and gravidity are derived from the record's fetus outcomes, and Kathryn's ruling is that
  // stillbirth remains a full outcome, not a live birth with a death attached. So the outcome
  // being written is now whatever the fetus already carries, and only a fetus with no settled
  // outcome at all is promoted to a live birth.
  const convertFetusToPerson = async (
    carrier,
    record,
    childGender,
    fetusIndex = 0
  ) => {
    if (busy || !carrier?._id || !record?._id || !user?._id) {
      if (!user?._id) {
        setSnack?.({
          status: true,
          msg: 'Cannot add the child right now — reopen the pedigree and try again',
          severity: 'warning'
        });
      }
      return false;
    }
    const rel =
      childGender === GENDER.male
        ? relationship.son
        : childGender === GENDER.female
        ? relationship.daughter
        : '';
    const relationshipValue = rel
      ? getRelationshipForNewPerson(carrier, user, rel)
      : '';
    // The fetus becoming this person. The conversion is offered only for a single-fetus pregnancy,
    // so this is unambiguous; normalizeFetuses covers a record written before fetuses existed.
    const fetuses = normalizeFetuses(record);
    const fetus = fetuses[fetusIndex] || {};
    // The outcome this conversion settles on. A fetus already recorded as a stillbirth stays one
    // (TGCLOUD-1616); anything else becomes a live birth, which is what every pre-1616 caller did.
    const isStillbirthConversion =
      fetus.outcome === PREGNANCY_OUTCOME.stillbirth;
    const settledOutcome = isStillbirthConversion
      ? PREGNANCY_OUTCOME.stillbirth
      : PREGNANCY_OUTCOME.liveBirth;
    const inherited = newbornInheritance(carrier, fetus);
    const child = {
      ...createPerson(childGender, user._id, [], relationshipValue),
      // Date of birth + prenatal diagnoses from the fetus; surname + address from the mother, and
      // NO identifiers (decision 7). One mapping, stated in liveBirthInheritance.
      ...inherited,
      // A stillborn person is deceased, and carries the Bennett SB annotation as a Person Note.
      // Both come from the same pure module, so the clinical rule is testable on its own.
      ...(isStillbirthConversion
        ? {
            ...stillbirthPersonFields(fetus),
            personNote: withStillbirthNote(inherited.personNote)
          }
        : {})
    };
    const childUid = child.uid;
    // Partner-conceived → the recorded other parent is the co-parent; donor-conceived → carrier
    // alone. `otherParentMemberId` is what the form actually stored, so a multi-spouse carrier
    // attaches the child to the RIGHT partner; falling back to the first spouse only when the
    // record predates that field.
    const recordedCoParent = (memberRecords || []).find((m) =>
      matchesMemberRef(m, record.otherParentMemberId)
    );
    const coParent = recordedCoParent || spouseOf(carrier);
    const coParentPid =
      (record.conceivedWith === 'partner' ||
        record.conceivedWith === 'partnerAndDonor') &&
      coParent
        ? coParent.pid
        : null;
    // The fetus is now a person: it must stop drawing its own symbol. The render derivation reads
    // `bornMemberId` PER FETUS, so stamping only the pregnancy-level field would leave the symbol on
    // the chart beside the child it became.
    const bornFetuses = fetuses.map((f, i) =>
      i === fetusIndex
        ? { ...f, outcome: settledOutcome, bornMemberId: childUid }
        : f
    );

    // TWIN LINKAGE (decision 18). The twin relationship lives on the derived fetus nodes, so unless it
    // is re-asserted on the real children it is lost at the moment of birth: converting both twins
    // would leave two ordinary siblings. Any co-fetus already converted is found through its
    // `bornMemberId`, and the group is stamped from the pregnancy's own multiplicity and zygosity via
    // the shared twinLinkage helper — the same source deriveReproNodes uses, so the drawn twin type
    // cannot disagree with the recorded one.
    const alreadyBornSiblingRefs = fetuses
      .filter((f, i) => i !== fetusIndex && f && f.bornMemberId != null)
      .map((f) => f.bornMemberId);
    const twinFields =
      fetuses.length > 1
        ? twinFieldsFor({ count: fetuses.length, zygosity: record.zygosity })
        : null;

    // The record-level `outcome` MIRRORS the first fetus — that is the convention buildPayload
    // already follows, because the legacy top-level fields are still read by the FHQ tables, the
    // PDF, the XML serializer and the record-history audit config.
    //
    // This used to be written as an unconditional `liveBirth`, which for a stillbirth conversion
    // would relabel the whole record as a live birth and move the derived parity. It also
    // contradicted buildPayload, so the value was overwritten by the mirror on the next ordinary
    // save anyway. Mirroring here makes the two paths agree.
    const recordOutcome = bornFetuses[0]?.outcome ?? settledOutcome;

    setBusy(true);
    try {
      // Persist the record link first (PregnancyChanged); the child persists on the save below.
      await axios.post(
        APICONSTANTS.probandPregnanciesDetails.replace(':user_id', carrier._id),
        {
          formValues: {
            ...record,
            fetuses: bornFetuses,
            outcome: recordOutcome,
            bornMemberId: childUid
          },
          isEditing: true
        }
      );
      // One state update: add the wired child, then stamp the carrier's record.
      setMemberRecords((prev) => {
        const withChild = addChildrenToRecords(prev, carrier.pid, {
          count: 1,
          coParentPid,
          makeChild: () => child
        });
        // Link this child to any co-twin already on the pedigree. Done here, inside the same state
        // update, so the twin fields and the child arrive together and no intermediate render shows
        // them as unrelated siblings.
        const linked =
          twinFields && alreadyBornSiblingRefs.length
            ? linkTwinMembers(
                withChild,
                [
                  child.pid,
                  ...alreadyBornSiblingRefs
                    .map(
                      (ref) =>
                        withChild.find((m) => matchesMemberRef(m, ref))?.pid
                    )
                    .filter((pid) => pid != null)
                ],
                twinFields
              )
            : withChild;
        return linked.map((m) =>
          m.pid === carrier.pid
            ? {
                ...m,
                pregnancies: (m.pregnancies || []).map((p) =>
                  String(p._id) === String(record._id)
                    ? {
                        ...p,
                        fetuses: bornFetuses,
                        outcome: recordOutcome,
                        bornMemberId: childUid
                      }
                    : p
                )
              }
            : m
        );
      });
      markSaving();
      setBusy(false);
      return true;
    } catch (err) {
      setSnack?.({
        status: true,
        msg: err?.response?.data?.message || 'Failed to record the live birth',
        severity: 'error'
      });
      setBusy(false);
      return false;
    }
  };

  return {
    busy,
    submitPregnancy,
    deletePregnancy,
    toggleHidden,
    convertFetusToPerson,
    createDonorPerson
  };
}
